import month from "@/helper/datetime/month";

import {defineStore} from "pinia";
import {getArrayElementsExcluding, getRandomArrayElements} from "@/helper/array";
import Fuse from 'fuse.js'
import {
    AbsenceType,
    AInesAssistantType,
    AInesFeatureSet,
    AInesJobsiteListType,
    AInesRichResponseType
} from "@/graphql/generated/graphql";
import date, {formatKapitelDate} from "@/helper/datetime/date";
import {
    addCircleOutline,
    calendarNumberOutline,
    calendarOutline, homeOutline,
    listOutline,
    medkitOutline, searchCircleOutline, syncCircleOutline,
    timerOutline
} from "ionicons/icons";
import {computed, ComputedRef, Ref} from "vue";
import {useAppState} from "@/helper/appState";
import {useCandidateStore} from "@/store/candidate";
import {QuickChat} from "@/helper/chat/quickChat";
import {RichContent} from "@/helper/chat/richResponses";
import {getIconForAbsenceType} from "@/helper/absence";
import {getMostRecentPublishedMonth} from "@/helper/monthKPI/monthKPI";
import {useAuthStore} from "@/store/auth";
import {KapitelIcon} from "@/helper/icon";

interface InternalQuickChat {
    role: "candidate"|"employee"
    promotionState?: boolean;
}

export interface ExploreQuickChat extends QuickChat {
    featureSet?: AInesFeatureSet,
    expertAssistant?: AInesAssistantType,
    richContentsPreview?: RichContent[],
    id: string,
}


export interface MainMenuQuickChat extends ExploreQuickChat {
    new: boolean
}

const exploreQuickChats: (ExploreQuickChat&InternalQuickChat)[] = [
    {
        id: "sickness_add",
        label: "Krank melden",
        prompt: "Ich muss mich krank melden.",
        featureSet: AInesFeatureSet.Sickness,
        icon: KapitelIcon.sick,
        role: 'employee',
        richContentsPreview: [{
            type: AInesRichResponseType.SicknessWizard,
            initData: undefined
        }]
    },
    {
        id: "next_booking_show",
        label: "Nächster Dienst",
        prompt: "Was ist mein nächste Dienst?",
        featureSet: AInesFeatureSet.Roster,
        icon: KapitelIcon.booking,
        role: 'employee',
    },
    {
        id: "next_week_show",
        label: "Kommende Woche",
        prompt: "Wie sieht mein Dienstplan für kommende Woche aus?",
        featureSet: AInesFeatureSet.Roster,
        icon: KapitelIcon.itemList,
        role: 'employee',
        richContentsPreview: [{
            type: AInesRichResponseType.WeekCalendar,
            initData: date.startOfWeek(date.addWeeks(date.getToday(), 1))
        }]
    },
    {
        id: "next_seven_days_show",
        label: "Nächste 7 Tage",
        prompt: "Wie sieht mein Dienstplan für die nächsten 7 Tage aus?",
        featureSet: AInesFeatureSet.Roster,
        icon: KapitelIcon.itemList,
        role: 'employee',
        richContentsPreview: [{
            type: AInesRichResponseType.WeekCalendar,
            initData: date.getToday()
        }]
    },
    {
        id: "next_months_show",
        label: "Dienstplan " + formatKapitelDate(date.addMonths(date.getToday(), 1) , 'MMMM'),
        prompt: "Wie sieht mein Dienstplan für den nächsten Monat aus?",
        featureSet: AInesFeatureSet.Roster,
        icon: KapitelIcon.shiftPlan,
        role: 'employee',
        richContentsPreview: [{
            type: AInesRichResponseType.MonthCalendar,
            initData: date.startOfMonth(date.addMonths(date.getToday(), 1))
        }]
    },
    {
        id: "next_months_show",
        label: "Dienstplan " + formatKapitelDate(date.addMonths(date.getToday(), 2) , 'MMMM'),
        prompt: "Wie sieht mein Dienstplan für den "+ formatKapitelDate(date.addMonths(date.getToday(), 2) , 'MMMM')+" aus?",
        featureSet: AInesFeatureSet.Roster,
        icon: KapitelIcon.shiftPlan,
        role: 'employee',
        richContentsPreview: [{
            type: AInesRichResponseType.MonthCalendar,
            initData: date.startOfMonth(date.addMonths(date.getToday(), 2))
        }]

    },
    {
        id: "timesheets_show",
        label: "Zeiterfassung",
        prompt: "Für welche Dienste muss ich noch Zeiten erfassen?",
        featureSet: AInesFeatureSet.Timesheet,
        icon: KapitelIcon.timesheet,
        role: 'employee',
        richContentsPreview: [{
            type: AInesRichResponseType.TimesheetJobsiteSummary,
            initData: undefined
        }]
    },
    {
        id: "planning_next_month_show",
        label: "Planungsstatus " + formatKapitelDate(date.addMonths(date.getToday(), 1) , 'MMMM'),
        prompt: "Wie steht es um die Planung für " + formatKapitelDate(date.addMonths(date.getToday(), 1) , 'MMMM') + "?",
        featureSet: AInesFeatureSet.PlanningMonth,
        icon: KapitelIcon.planning,
        role: 'employee',
        richContentsPreview: [{
            type: AInesRichResponseType.AutopilotPlanningStatusSummary,
            initData: date.startOfMonth(date.addMonths(date.getToday(), 1))
        }]
    },
    {
        id: "planning_this_month_show",
        label: "Planungsstatus " + formatKapitelDate(date.getToday(), 'MMMM'),
        prompt: "Wie steht es um die Planung für " + formatKapitelDate(date.getToday() , 'MMMM') + "?",
        featureSet: AInesFeatureSet.PlanningMonth,
        icon: KapitelIcon.planning,
        role: 'employee',
        richContentsPreview: [{
            type: AInesRichResponseType.AutopilotPlanningStatusSummary,
            initData: date.startOfMonth(date.getToday())
        }]
    },
    {
        id: "planning_months_show",
        label: "Welche Monate sind aktuell in Dienstplanung?",
        featureSet: AInesFeatureSet.PlanningMonth,
        icon: KapitelIcon.planning,
        role: 'employee',
    },
    // {
    //     id: "recruiting_start",
    //     label: "Ich kenne jemanden, der zur IBFG will!",
    //     featureSet: AInesFeatureSet.Recruiting,
    // },


    {
        id: "jobsite_template_show",
        label: "Stationsauswahl für Dienstplanung",
        prompt: "Wie sieht meine aktuelle Stationsauswahl für " + formatKapitelDate(date.getToday() , 'MMMM') + " aus?",
        featureSet: AInesFeatureSet.PlanningMonth,
        icon: KapitelIcon.planning,
        role: 'employee',
        richContentsPreview: [{
            type: AInesRichResponseType.AutopilotJobsitePreferences,
            initData: date.startOfMonth(date.getToday())
        }]
    },
    {
        id: "jobsite_preferences_show",
        label: "Lieblingsstationen anzeigen",
        prompt: "Was sind aktuell meine Lieblingsstationen?",
        featureSet: AInesFeatureSet.JobsitePreferences,
        icon: KapitelIcon.favoriteJobsite,
        richContentsPreview: [{
            type: AInesRichResponseType.JobsiteList,
            initData: AInesJobsiteListType.Favorite
        }],
        role:'employee'
    },
    {
        id: "jobsite_preferences_alternatives_show",
        label: "Alternativstationen anzeigen",
        prompt: "Was sind aktuell meine Alternativstationen?",
        featureSet: AInesFeatureSet.JobsitePreferences,
        icon: KapitelIcon.alternativeJobsite,
        richContentsPreview: [{
            type: AInesRichResponseType.JobsiteList,
            initData: AInesJobsiteListType.Alternative
        }],
        role:'employee'
    },

    {
        id: "planning_months_show",
        label: "Monatsauswertung " + month.formatKapitelMonth(getMostRecentPublishedMonth()),
        prompt: "Zeig mir die Monatsauswertung (Vereinszahlen) für " + month.formatKapitelMonth(getMostRecentPublishedMonth()),
        featureSet: AInesFeatureSet.Kpi,
        icon: KapitelIcon.kpi,
        role: 'employee',
    },
    {
        id: "AZK_show",
        label: "Arbeitszeitkonto",
        prompt: "Wie viele Stunden sind auf meinem AZK?",
        featureSet: AInesFeatureSet.Kpi,
        icon: KapitelIcon.kpi,
        role: 'employee',
    },
    {
        id: "vacation_show",
        label: "Resturlaub",
        prompt: "Wie viel Urlaubsanspruch habe ich noch?",
        featureSet: AInesFeatureSet.Kpi,
        icon: KapitelIcon.vacation,
        role: 'employee',
    },

    // {
    //     id: "azk_show",
    //     label: "Wie stehts um mein AZK?",
    //     featureSet: AInesFeatureSet.PlanningMonth,
    // },

    {
        id: "suggest_jobsite",
        label: "Schlag mir eine Station vor",
        prompt: "Schlag mir doch mal eine Station vor, auf der ich, basierend auf meinen Qualifikationen, arbeiten könnte",
        expertAssistant: AInesAssistantType.CandidateAssistant,
        icon: KapitelIcon.jobsite,
        role: 'candidate',
    },
    {
        id: "suggest_demand",
        label: "Schlag mir einen Dienst vor",
        prompt: "Schlag mir doch mal einen möglichen Zusatzdienst vor, basierend auf meinen Qualifikationen",
        expertAssistant: AInesAssistantType.CandidateAssistant,
        icon: KapitelIcon.booking,
        role: 'candidate',
    },
    {
        id: "what_is_ibfg",
        label: "Was ist die IBFG?",
        prompt: "Was ist denn die IBFG und was habe ich für Vorteile, wenn ich dort anfange zu arbeiten bzw. Zusatzdienste mache?",
        expertAssistant: AInesAssistantType.CandidateAssistant,
        icon: KapitelIcon.question,
        role: 'candidate',
        promotionState: false
    },
    {
        id: "start_working",
        label: "Bewerben",
        prompt: "Ich möchte gerne eine Anstellung bei der IBFG haben.",
        expertAssistant: AInesAssistantType.CandidateAssistant,
        icon: KapitelIcon.employee,
        role: 'candidate',
        promotionState: false
    },
    {
        id: "show_jobsites",
        label: "Merkliste der Stationen",
        prompt: "Zeig mir die Stationen auf meiner Merkliste.",
        expertAssistant: AInesAssistantType.CandidateAssistant,
        icon: KapitelIcon.jobsite,
        role: 'candidate',
        promotionState: true
    },
    {
        id: "find_demand",
        label: "Dienst finden",
        prompt: "Gibt es auf den Stationen auf meiner Merkliste gerade offene Dienste für mich?",
        expertAssistant: AInesAssistantType.CandidateAssistant,
        icon: KapitelIcon.booking,
        role: 'candidate',
        promotionState: true
    },

    {
        id: "calendar_integration",
        label: "Kalenderintegration",
        prompt: "Wir kann ich meinen Dienstplan mit meinem privaten Kalender synchronisieren?",
        expertAssistant: AInesAssistantType.FeatureOverview,
        icon: KapitelIcon.calendarSync,
        role: 'employee'
    },
    {
        id: "booking_add",
        label: "Neuen Dienst eintragen",
        prompt: "Ich möchte einen Dienst eintragen.",
        featureSet: AInesFeatureSet.RegisterBooking,
        icon: KapitelIcon.registerBooking,
        role: 'employee',
        richContentsPreview: [{
            type: AInesRichResponseType.RegisterBookingSummary,
            initData: {
                jobsiteSearchString: undefined,
                dates: undefined,
                shift: undefined
            }
        }]
    },
    {
        id: "stoebern",
        label: "Offene Dienste finden",
        prompt: "Welche offenen Dienste gibt es aktuell auf meinen Lieblingsstationen?",
        featureSet: AInesFeatureSet.EmployeeDemandBrowser,
        icon: KapitelIcon.stoebern,
        role: 'employee',
        // richContentsPreview: [{
        //     type: AInesRichResponseType.Stoebern,
        //     initData: undefined
        // }]
    },
    {
        id: "suggest_jobsite_employee",
        label: "Neue Station vorschlagen",
        prompt: "Schlag mir doch mal eine neue, mir bislang möglichste unbekannte Station mit offenen Diensten vor, auf der ich arbeiten könnte.",
        expertAssistant: AInesAssistantType.CandidateAssistant,
        icon: KapitelIcon.stoebern,
        role: 'candidate',
    },
    {
        id: "suggest_demand_employee",
        label: "Dienst vorschlagen",
        prompt: "Schlag mir doch mal einen beliebigen offenen Dienst vor.",
        expertAssistant: AInesAssistantType.CandidateAssistant,
        icon: KapitelIcon.stoebern,
        role: 'candidate',
    },
    // Dienste tauschen
]

const matchesCurrentRole = (qc: InternalQuickChat) => {
    const role = useAppState().role.value
    const isPromoted = role === 'candidate' && useCandidateStore().isPromoted

    return qc.role === role && ((typeof qc.promotionState === 'undefined') || qc.promotionState === isPromoted)
}

const exploreQuickChatsForCurrentRole : Ref<(ExploreQuickChat & InternalQuickChat)[]> = computed(() => exploreQuickChats.filter(matchesCurrentRole))

const getExploreQuickChatById = (id: string) : ExploreQuickChat & InternalQuickChat | undefined => {
    return exploreQuickChats.find(qc => qc.id === id)
}
const getExploreQuickChatByIds = (ids: string[]) : (ExploreQuickChat & InternalQuickChat)[] => {
    const result : (ExploreQuickChat & InternalQuickChat)[] = [];
    ids.forEach(id => {
        const qc = getExploreQuickChatById(id)
        if (qc) {
            result.push(qc)
        }
    })
    return result
}

export const exploreQuickChatMainMenuItems: ComputedRef<(MainMenuQuickChat&InternalQuickChat)[]> = computed(() =>
    getExploreQuickChatByIds([
        'calendar_integration',
        'booking_add',
        'stoebern'
    ])
        .filter(matchesCurrentRole)
        .map(qc => ({...qc, new: useExploreQuickChatStore().isNew(qc.id)}))
)

export const useExploreQuickChatStore = defineStore("exploreQuickChatHistory", {
    persist: true,
    state: (): {
        usages: { [key:string]: number },
    } => ({
        usages: {},
    }),
    getters: {
        used: (state) => getExploreQuickChatByIds(Object.keys(state.usages)),
        isUsed() {
            return (key: string) => (this.usages[key] ?? 0) > 0
        },
        isNew(state) {
            return (key: string) => {
                switch (key) {
                    case 'calendar_integration':
                        return false
                    case 'booking_add':
                        return false
                    case 'stoebern':
                        return !this.isUsed(key)
                    default:
                        return false
                }
            }
        },
    },
    actions: {
        trackUsage(qc: ExploreQuickChat) {
            this.usages[qc.id] = (this.usages[qc.id] ?? 0) + 1
        },
    },
});

let fuse : ComputedRef<Fuse<ExploreQuickChat>> = computed(() => new Fuse(
    exploreQuickChatsForCurrentRole.value,
    {
        keys: ['label', 'prompt'],
        includeScore: true,
        threshold: 0.4
    }
))

export const getExploreQuickChatsMatching = (s: string, limitN: number) : ExploreQuickChat[] => {
    const fuseResults = fuse.value.search(s)

    return fuseResults
        .slice(0, limitN)
        .map(result => result.item)
}



// always return at least one random pick together with most used
export const getExploreQuickChatsTopN = (n: number, excludeMainMenuItems = true) : ExploreQuickChat[] => {

    const mainMenuItems = exploreQuickChatMainMenuItems.value

    // get already used
    const store = useExploreQuickChatStore()
    let usedQCs = store.used
    if (excludeMainMenuItems) {
        usedQCs = getArrayElementsExcluding(usedQCs, mainMenuItems)
    }

    // pick at most n-1 already used
    const pickedUsedQCs = usedQCs.length > n - 1
        ? getRandomArrayElements(usedQCs, n - 1)
        : usedQCs

    // collect at least 1 random
    const missingQuickChatCount = n - pickedUsedQCs.length
    let remainingQCs = getArrayElementsExcluding(exploreQuickChatsForCurrentRole.value, usedQCs)
    if (excludeMainMenuItems) {
        remainingQCs = getArrayElementsExcluding(remainingQCs, mainMenuItems)
    }
    const resultRandomQCs = getRandomArrayElements(remainingQCs, missingQuickChatCount)

    // return between n-1 & n quick chats
    return pickedUsedQCs.concat(...resultRandomQCs)
}
